import { useEffect, useRef, useState } from "react";

/** How long a change takes to land — long enough to read as motion, short enough that a
 *  crown tapped twice a second never lags behind its own number. */
const COUNT_UP_MS = 400;

/**
 * A number that rolls to its new value instead of jumping — the Faps pill and the wallet's
 * hero, when a tap, a chest or a purchase moves the balance.
 *
 * The rules that keep it honest:
 * - The DISPLAYED value starts AT the value. First paint shows the real number (the static
 *   check harness renders this without a browser and must see it too); nothing counts up
 *   from zero on mount.
 * - Only a number → number change animates. `null → number` (sign-in, the store's reset)
 *   snaps, because there is no "from" to roll from.
 * - A target that changes mid-roll restarts from the number ON SCREEN, never from the old
 *   target — two quick taps read as one continuous roll.
 * - `prefers-reduced-motion` snaps. The client's reduced-motion handling is a CSS block
 *   (index.css) for classes; a JS tween has to ask for itself, and only inside the effect —
 *   a half-built `window` in the harness has no `matchMedia` (home-surface.mts).
 * - Every frame is cancelled on unmount and on restart: a tween writing into a dead
 *   component is the same hazard useCrownBursts guards against.
 *
 * Integers only: a balance has no fractions, and a rolling "1,239.6" would read as one.
 */
export function useCountUp(value: number | null): number | null {
  const [shown, setShown] = useState<number | null>(value);
  const shownRef = useRef<number | null>(value);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    const from = shownRef.current;
    const to = value;
    const cancel = () => {
      if (frame.current !== null && typeof cancelAnimationFrame === "function") cancelAnimationFrame(frame.current);
      frame.current = null;
    };
    cancel();
    const snap = () => {
      shownRef.current = to;
      setShown(to);
    };
    if (to === null || from === null || from === to) {
      snap();
      return cancel;
    }
    const reduced =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || typeof requestAnimationFrame !== "function") {
      snap();
      return cancel;
    }
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / COUNT_UP_MS);
      // Ease-out: fast off the mark, settling onto the number.
      const eased = 1 - (1 - t) * (1 - t) * (1 - t);
      const next = t >= 1 ? to : Math.round(from + (to - from) * eased);
      shownRef.current = next;
      setShown(next);
      if (t < 1) frame.current = requestAnimationFrame(step);
      else frame.current = null;
    };
    frame.current = requestAnimationFrame(step);
    return cancel;
  }, [value]);

  return shown;
}
